import { ApolloError } from "@apollo/client";
import React, {
    ReactElement,
} from "react";
import lottieImg from "../../assets/lottie/404.lottie";
import securityImg from "../../assets/lottie/data-security.lottie";
import Player from "../../components/LottiePlayer";
import useRouting, { Paths } from "../../contexts/Routing";

interface ErrorProps {
    message: string;
    title?: string;
    stacktrace?: string[] | undefined;
    image?: string;
    shouldShowLogin?: boolean;
}

export function ErrorView(props :ErrorProps): ReactElement {
    const { createHistoryPush } = useRouting();
    return (
        <>
            <Player
                autoplay
                loop
                src={props.image ?? lottieImg}
                className="player"
            />
            <div>
                <h3 className="mb-4">{props.title}</h3>
                <div className="font-light mb-3">{props.message}</div>
                {props.stacktrace && (
                    <div className="alert alert-secondary">
                        <pre className="text-start">
                            <code>
                                {props.stacktrace.join("\n")}
                            </code>
                        </pre>
                    </div>
                )}
                {props.shouldShowLogin && (
                    <button type="button" onClick={createHistoryPush(Paths.LOGIN)} className="btn btn-primary px-4 btn">
                        Login
                    </button>
                )}
            </div>
        </>
    );
}

ErrorView.defaultProps = {
    title: "Oops",
    stacktrace: undefined,
    image: lottieImg,
    shouldShowLogin: false,
};

export function ErrorDefault(props :ErrorProps): ReactElement {
    return (
        <div className="full-page">
            <div className={`centered-container${props.stacktrace ? "-lg" : ""} shadow-lg border p-4 text-center`}>
                <ErrorView {...props} />
            </div>
        </div>
    );
}
ErrorDefault.defaultProps = {
    title: "Oops",
    stacktrace: undefined,
    image: lottieImg,
    shouldShowLogin: false,
};

export function Error404(): ReactElement {
    return (
        <ErrorDefault
            message="We have no idea how you are getting this page, but we don&apos;t know where it is either."
        />
    );
}

export function ErrorSecurity({ message, title }: {message: string, title?: string}): ReactElement {
    return (
        <ErrorDefault
            title={title}
            image={securityImg}
            message={message}
            shouldShowLogin
        />
    );
}
ErrorSecurity.defaultProps = {
    title: "Not Authorized",
};

export function ErrorRemote({ error }: {error?: ApolloError}): ReactElement {
    const firstError = error?.graphQLErrors?.[0];
    const firstException = firstError?.extensions?.exception as Error & ({stacktrace: string[]}) | undefined;
    return (
        <ErrorDefault
            title={firstError?.message ?? "Internal Error"}
            message={firstException?.name ?? error?.message ?? "Error downloading from the server."}
            stacktrace={firstException?.stacktrace}
        />
    );
}
ErrorRemote.defaultProps = {
    error: undefined,
};

export function ErrorRemoteView({ error }: {error?: ApolloError}): ReactElement {
    const firstError = error?.graphQLErrors?.[0];
    const firstException = firstError?.extensions?.exception as Error & ({stacktrace: string[]}) | undefined;

    return (
        <ErrorView
            title={firstError?.message ?? "Internal Error"}
            message={firstException?.name ?? error?.message ?? "Error downloading from the server."}
            stacktrace={firstException?.stacktrace}
        />
    );
}
ErrorRemoteView.defaultProps = {
    error: undefined,
};

export function NoDataOrError({ error }: {error?: ApolloError}): ReactElement {
    return error ? <ErrorRemote error={error} /> : (
        <ErrorDefault
            title="Error Connecting"
            message="No data returned."
        />
    );
}
NoDataOrError.defaultProps = {
    error: undefined,
};

export function NoDataOrErrorView({ error }: {error?: ApolloError}): ReactElement {
    return error ? <ErrorRemoteView error={error} /> : (
        <ErrorView
            title="Error Connecting"
            message="No data returned."
        />
    );
}
NoDataOrErrorView.defaultProps = {
    error: undefined,
};
